/**
 * @module app.PoiLayers3d
 */
import olGeomPoint from 'ol/geom/Point.js';
import appFeatureConverter from './FeatureConverter.js';
import olFormatGeoJSON from 'ol/format/GeoJSON.js';
import olLayerVector from 'ol/layer/Vector.js';
import olSourceVector from 'ol/source/Vector.js';
import olStyleIcon from 'ol/style/Icon.js';
import olStyleStyle from 'ol/style/Style.js';
import {SLOWUP_CATEGORIES} from './constants.js';

/**
 * @param {!Cesium.Scene} scene
 * @param {string} poiLayersDataUrl
 * @param {string} poiLayers3dImgUrl
 * @param {app.CacheBustService} appCacheBustService
 * @param {ngeo.statemanager.Location} ngeoLocation ngeo Location service.
 * @constructor
 * @struct
 * @export
 */
export default class {
  constructor(
    scene,
    poiLayersDataUrl,
    poiLayers3dImgUrl,
    appCacheBustService,
    ngeoLocation
  ) {
    /**
     * @type {Object.<string, Cesium.BillboardCollection>}
     */
    this.bbMap = {};

    /**
     * @type {Object.<string, Object.<string, ol.Feature>>}
     */
    this.featuresMap = {};

    /**
     * @type {string}
     */
    this.poiLayersDataUrl = poiLayersDataUrl;

    /**
     * @type {string}
     */
    this.poiLayers3dImgUrl = poiLayers3dImgUrl;

    /**
     * @private
     */
    this.cacheBustService_ = appCacheBustService;

    this.scene = scene;
    this.featureConverter = new appFeatureConverter(scene, ngeoLocation);

    // FIXME: it would be better to read these from the geojson file
    // even if there is no standard way to do it.
    this.clusterResolutions = [0, 20, 100, 250, 500, 1000, 1500, 2000, 3000];
    this.format = new olFormatGeoJSON();
  }

  /**
   * @param {string} code layer code like 'Alpentaxi'
   * @param {boolean} activate
   */
  updateLayer(code, activate) {
    let billboards = this.bbMap[code];

    if (!billboards) {
      // Create empty billboard collection
      billboards = new Cesium.BillboardCollection({scene: this.scene});
      this.bbMap[code] = billboards;

      // Populate it asynchronously
      if (code !== 'Alpentaxi' && code !== 'Slowup') {
        this.createStyle_(code).then((style) => {
          console.assert(billboards);
          console.assert(style);
          this.createCesiumPrimitives_(billboards, code, () => style);
        });
      } else {
        // In the case of Alpentaxi we have 3 subtypes thus 3 styles to choose from.
        // We preload the images and create a stylefunction to handle the selection.
        const subtypes = code === 'Slowup' ? SLOWUP_CATEGORIES : ['Rufbus', 'Taxi', 'Seilbahn'];
        const subtypeStylesPromises = subtypes.map((subtype) => this.createStyle_(subtype));
        Promise.all(subtypeStylesPromises).then((styles) => {
          const styleMappings = {};
          for (let i = 0; i < styles.length; ++i) {
            styleMappings[subtypes[i]] = styles[i];
          }
          const styleFunction = (feature, resolution) => {
            const subtype = /** @type {string} */ (feature.get('subtype'));
            console.assert(subtype);
            return styleMappings[subtype];
          };

          console.assert(billboards);
          this.createCesiumPrimitives_(billboards, code, styleFunction);
        });
      }
    }

    const primitives = this.scene.primitives;
    if (activate) {
      if (!primitives.contains(billboards)) {
        primitives.add(billboards);
      }
    } else {
      if (primitives.contains(billboards)) {
        const shouldDestroy = primitives.destroyPrimitives;
        primitives.destroyPrimitives = false;
        primitives.remove(billboards);
        primitives.destroyPrimitives = shouldDestroy;
      }
    }
  }

  /**
   * @private
   * @param {!Cesium.BillboardCollection} billboards
   * @param {!string} code
   * @param {!function(ol.Feature, number): !ol.style.Style} styleFunction
   * @return {jQuery.Promise}
   */
  createCesiumPrimitives_(billboards, code, styleFunction) {
    const format = this.format;
    console.assert(billboards.length === 0);
    const featureConverter = this.featureConverter;
    const layer = new olLayerVector({
      'name': 'pois 3d',
      source: new olSourceVector(),
      'code': code,
      'type': '3dpoi'
    });

    const featuresMap = this.featuresMap[code] = {};
    const url = this.poiLayersDataUrl + '4326/clustered_' + code + '.geojson';
    return $.getJSON(url)
      .done(function(json) {
        const features = format.readFeatures(json);

        features.forEach(function(f) {
          console.assert(f);
          const point = f.getGeometry();

          /**
           * @param {Cesium.Billboard} bb
           */
          function id(bb) {
            bb.olFeature = f;
            bb.olLayer = layer;
          }

          console.assert(point instanceof olGeomPoint);
          const style = styleFunction(f, 0);
          featureConverter.olPointGeometryToCesium(layer, f, point, 'EPSG:4326', style, billboards, id);
          featuresMap[f.getId().toString()] = f;
        });
      });
  }

  /**
   * @private
   * @param {string} code
   * @return {Promise<!ol.style.Style>}
   */
  createStyle_(code) {
    const isSlowup = SLOWUP_CATEGORIES.includes(code);
    const iconSrc = isSlowup ? `img/slowup/${code.toLowerCase()}.gif` : `${this.poiLayers3dImgUrl}3d_${code}_22.png`;
    const url = this.cacheBustService_.addSuffix(iconSrc);
    return Cesium.Resource.fetchImage(url)
      .then(function(img) {
        const style = new olStyleStyle({
          image: new olStyleIcon({
            anchor: [0.5, 1.0],
            opacity: 1.0,
            imgSize: [img.naturalWidth, img.naturalHeight],
            img: img
          })
        });
        return style;
      });
  }

  /**
   * @param {string} layerCode
   * @param {ol.Feature} feature
   * @param {Cesium.Scene} scene
   * @param {Object.<string, number>} found
   * @return {Object.<string, number>}
   */
  findChildrenIds(layerCode, feature, scene, found) {

    const poiGeometry = feature.getGeometry();
    console.assert(poiGeometry instanceof olGeomPoint);
    const poiPosition = poiGeometry.getCoordinates();
    const poiCartographic = Cesium.Cartographic.fromDegrees(poiPosition[0], poiPosition[1]);

    const height = scene.globe.getHeight(poiCartographic) || 0; // FIXME?
    const poiCartesian = Cesium.Cartesian3.fromDegrees(poiPosition[0], poiPosition[1], height);

    const cameraCartesian = scene.camera.position;
    const distance = Cesium.Cartesian3.distance(poiCartesian, cameraCartesian);

    const resolution = distance / this.featureConverter.resolutionDistanceFactor;
    const featuresMap = this.featuresMap[layerCode];
    const id = /** @type {number} */ (feature.getId());
    found[id.toString()] = id;
    this.findChildrenIds_(featuresMap, feature, resolution, found);
    return found;
  }

  /**
   * @param {Object.<string, ol.Feature>} featuresMap
   * @param {ol.Feature} feature
   * @param {number} resolution
   * @param {Object.<string, number>} found
   * @private
   */
  findChildrenIds_(featuresMap, feature, resolution, found) {
    const children = feature.get('children');
    if (!children) {
      return;
    }

    const clusterResolutions = this.clusterResolutions;

    // Travel across all children at resolutions smaller or equal to resolution.
    for (let i = clusterResolutions.length - 1; i > -1; --i) {
      const r = clusterResolutions[i];
      if (r > resolution) {
        continue;
      }

      const currChildren = children[r] || {};
      for (const idx in currChildren) {
        const id = currChildren[idx];
        found[id.toString()] = id;
        const childFeature = featuresMap[id];
        this.findChildrenIds_(featuresMap, childFeature, resolution - 1, found);
      }
    }
  }
}
